'use client'

import React from 'react'
import ShareSection from '@/app/articles/[post]/_components/ShareSection'
import ArticleImages from '@/app/articles/[post]/_components/ArticleImages'
import Typography from '@/components/Typography'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@radix-ui/react-accordion'
import styles from '@/app/articles/[post]/styles.module.css'
import { getStrapiImageUrl } from '@/lib/strapi'

interface StrapiContentRendererProps {
  content: string | any
  faq?: Array<{ question: string; answer: string }>
  contentImages?: any // Может быть массивом, объектом с data, или объектом изображения
}

// Функция для конвертации Rich Text в HTML
export const convertRichTextToHTML = (richText: any): string => {
  if (!richText || !Array.isArray(richText)) {
    return ''
  }
  
  return richText.map((block: any) => {
    switch (block.type) {
      case 'paragraph':
        return `<p>${block.children?.map((child: any) => child.text || '').join('') || ''}</p>`
      case 'heading':
        const level = block.level || 2
        return `<h${level}>${block.children?.map((child: any) => child.text || '').join('') || ''}</h${level}>`
      case 'list':
        const listTag = block.format === 'ordered' ? 'ol' : 'ul'
        const items = block.children?.map((item: any) => 
          `<li>${item.children?.map((child: any) => child.text || '').join('') || ''}</li>`
        ).join('') || ''
        return `<${listTag}>${items}</${listTag}>`
      case 'image':
        const imageUrl = block.image?.url || ''
        const altText = block.image?.alternativeText || ''
        return `<img src="${getStrapiImageUrl(imageUrl)}" alt="${altText}" />`
      default:
        return ''
    }
  }).join('')
}

// Функция для рендеринга текста с форматированием (bold, italic, underline)
const renderFormattedText = (child: any): React.ReactNode => {
  if (!child || child.type !== 'text') {
    return child.text || ''
  }
  
  const { text, bold, italic, underline } = child
  if (!text) return ''
  
  let content: React.ReactNode = text
  const style = { fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 'inherit' }
  
  if (underline) content = <u style={style}>{content}</u>
  if (italic) content = <em style={style}>{content}</em>
  if (bold) content = <strong style={{ ...style, fontWeight: 'bold' }}>{content}</strong>
  
  return content
}

/**
 * Обрабатывает специальные теги в тексте из Strapi
 * Заменяет [WHITE-TEXT] на span с белым цветом
 * Работает как для отдельных слов, так и для целых абзацев
 * 
 * @param text - текст с тегами [WHITE-TEXT]
 * @returns HTML строка с замененными тегами
 */
export function processStrapiTextTags(text: string): string {
  if (!text) return ''
  
  let processed = text
  
  // Обрабатываем парные теги [WHITE-TEXT]...[/WHITE-TEXT]
  processed = processed.replace(
    /\[WHITE-TEXT\](.*?)\[\/WHITE-TEXT\]/gi,
    '<span style="color: white;">$1</span>'
  )
  
  // Обрабатываем одиночные теги [WHITE-TEXT] (для случаев, когда закрывающий тег отсутствует)
  // Ищем текст между [WHITE-TEXT] и следующим [WHITE-TEXT] или концом строки
  processed = processed.replace(
    /\[WHITE-TEXT\]([^\[]*?)(?=\[WHITE-TEXT\]|$)/gi,
    '<span style="color: white;">$1</span>'
  )
  
  return processed
}

// Функция для рендеринга Rich Text в React компонентах с правильными CSS классами
const renderRichTextContent = (richText: any): React.ReactNode => {
  if (!richText || !Array.isArray(richText)) {
    return null
  }
  
  // Отладочная информация (можно убрать в продакшене)
  // console.log('Rich Text blocks:', richText.map((block: any) => ({ type: block.type, text: block.children?.map((c: any) => c.text).join('') })))
  // console.log('Looking for special blocks...')
  
  // Группируем элементы в секции и изображения, а также собираем CTA/QUOTE блоки
  const sections: any[] = []
  let currentSection: any[] = []
  let currentImages: any[] = []
  let ctaBlocks: any[] = []
  let isCollectingCTA = false
  let quoteBlocks: any[] = []
  let isCollectingQuote = false
  let listNumberedBlocks: any[] = []
  let isCollectingListNumbered = false
  
  richText.forEach((block: any, index: number) => {
    // Получаем текст из любого блока (paragraph, heading, etc.)
    const text = block.children?.map((child: any) => child.text || '').join('') || ''
    
    // Проверяем специальные блоки для всех типов
    // CTA блоки
    if (text.includes('[CTA]') && !isCollectingCTA) {
      // Проверяем, есть ли закрывающий тег в том же блоке
      if (text.includes('[/CTA]')) {
        const ctaMatch = text.match(/\[CTA\]([\s\S]*?)\[\/CTA\]/)
        if (ctaMatch) {
          const ctaText = ctaMatch[1].trim()
          
          currentSection.push({
            type: 'cta',
            text: ctaText
          })
          return
        }
      }
      
      // Если закрывающего тега нет, начинаем сборку
      isCollectingCTA = true
      ctaBlocks = []
      // Извлекаем текст после [CTA]
      const afterCTA = text.split('[CTA]')[1]?.trim()
      if (afterCTA) {
        ctaBlocks.push({ children: [{ text: afterCTA }] })
      }
      return
    }
    
    if (text.includes('[/CTA]') && isCollectingCTA) {
      isCollectingCTA = false
      // Извлекаем текст до [/CTA]
      const beforeEndCTA = text.split('[/CTA]')[0]?.trim()
      if (beforeEndCTA) {
        ctaBlocks.push({ children: [{ text: beforeEndCTA }] })
      }
      
      // Создаем CTA блок
      const ctaText = ctaBlocks.map(ctaBlock => 
        ctaBlock.children?.map((child: any) => child.text || '').join('') || ''
      ).join(' ')
      
      currentSection.push({
        type: 'cta',
        text: ctaText
      })
      ctaBlocks = []
      return
    }
    
    if (isCollectingCTA) {
      ctaBlocks.push(block)
      return
    }
    
    // QUOTE блоки
    if (text.includes('[QUOTE]') && !isCollectingQuote) {
      isCollectingQuote = true
      quoteBlocks = []
      // Извлекаем текст после [QUOTE] и создаем блок с правильным типом
      const afterQUOTE = text.split('[QUOTE]')[1]?.trim()
      if (afterQUOTE) {
        // Сохраняем оригинальный тип блока и все его свойства
        quoteBlocks.push({ 
          type: block.type,
          level: block.level, // Сохраняем уровень заголовка
          children: [{ text: afterQUOTE }] 
        })
      }
      return
    }
    
    if (text.includes('[/QUOTE]') && isCollectingQuote) {
      isCollectingQuote = false
      // Извлекаем текст до [/QUOTE] и создаем блок с правильным типом
      const beforeEndQUOTE = text.split('[/QUOTE]')[0]?.trim()
      if (beforeEndQUOTE) {
        // Сохраняем оригинальный тип блока и все его свойства
        quoteBlocks.push({ 
          type: block.type,
          level: block.level, // Сохраняем уровень заголовка
          children: [{ text: beforeEndQUOTE }] 
        })
      }
      
      // Создаем QUOTE блок с сохранением структуры
      currentSection.push({
        type: 'quote',
        blocks: quoteBlocks // Передаем массив блоков вместо объединенного текста
      })
      quoteBlocks = []
      return
    }
    
    if (isCollectingQuote) {
      // Сохраняем оригинальный тип блока и все его свойства
      quoteBlocks.push({ 
        type: block.type,
        level: block.level, // Сохраняем уровень заголовка
        children: block.children 
      })
      return
    }
    
    // Проверяем, начинается ли LIST-NUMBERED блок
    if (text.includes('[LIST-NUMBERED]') && !isCollectingListNumbered) {
      // Проверяем, есть ли закрывающий тег в том же блоке
      if (text.includes('[/LIST-NUMBERED]')) {
        // Если оба тега в одном блоке, извлекаем содержимое между ними
        const listMatch = text.match(/\[LIST-NUMBERED\]([\s\S]*?)\[\/LIST-NUMBERED\]/)
        if (listMatch) {
          const listContent = listMatch[1].trim()
          // Разделяем содержимое по строкам и обрабатываем каждый элемент
          const items = listContent
            .split('\n')
            .filter((item: string) => item.trim()) // Фильтруем пустые строки
            .map((itemText: string, index: number) => {
              const parts = itemText.split('|')
              const label = parts[0]?.trim() || `Кейс ${index + 1}`
              const text = parts[1]?.trim() || ''
              // Пропускаем элементы, где и label и text пустые
              if (!label && !text) {
                return null
              }
              return { label, text }
            })
            .filter((item: { label: string; text: string } | null): item is { label: string; text: string } => item !== null)
          
          // Добавляем список только если есть элементы
          if (items.length > 0) {
            currentSection.push({
              type: 'listNumbered',
              items: items
            })
          }
          return
        }
      }
      
      // Если закрывающего тега нет, начинаем сборку
      isCollectingListNumbered = true
      listNumberedBlocks = []
      // Извлекаем текст после [LIST-NUMBERED]
      const afterList = text.split('[LIST-NUMBERED]')[1]?.trim()
      if (afterList) {
        // Создаем блок с извлеченным текстом
        listNumberedBlocks.push({ 
          type: block.type,
          children: [{ text: afterList }] 
        })
      }
      return
    }
    
    if (text.includes('[/LIST-NUMBERED]') && isCollectingListNumbered) {
      isCollectingListNumbered = false
      // Извлекаем текст до [/LIST-NUMBERED]
      const beforeEndList = text.split('[/LIST-NUMBERED]')[0]?.trim()
      if (beforeEndList) {
        listNumberedBlocks.push({ 
          type: block.type,
          children: [{ text: beforeEndList }] 
        })
      }
      
      // Создаем LIST-NUMBERED блок
      const listItems = listNumberedBlocks
        .map((listBlock, index) => {
          const itemText = listBlock.children?.map((child: any) => child.text || '').join('') || ''
          
          // Пропускаем пустые блоки
          if (!itemText.trim()) {
            return null
          }
          
          // Разделяем текст по символу | и берем первую часть как label
          const parts = itemText.split('|')
          const label = parts[0]?.trim() || `Кейс ${index + 1}`
          const text = parts[1]?.trim() || ''
          
          return {
            label: label,
            text: text
          }
        })
        .filter((item): item is { label: string; text: string } => item !== null)
      
      // Добавляем список только если есть элементы
      if (listItems.length > 0) {
        currentSection.push({
          type: 'listNumbered',
          items: listItems
        })
      }
      listNumberedBlocks = []
      return
    }
    
    if (isCollectingListNumbered) {
      // Пропускаем пустые блоки
      const blockText = block.children?.map((child: any) => child.text || '').join('') || ''
      if (blockText.trim()) {
        listNumberedBlocks.push(block)
      }
      return
    }
    
    // Если это обычный блок (не специальный), обрабатываем его как обычно
    // Заголовки всегда начинают новую секцию
    if (block.type === 'heading') {
      // Если есть накопленные изображения, добавляем их как группу
      if (currentImages.length > 0) {
        currentSection.push({ type: 'imageGroup', images: [...currentImages] })
        currentImages = []
      }
      if (currentSection.length > 0) {
        sections.push([...currentSection])
        currentSection = []
      }
      currentSection.push(block)
    } else if (block.type === 'image') {
      // Изображения накапливаем в группу
      currentImages.push(block)
    } else {
      // Если есть накопленные изображения, добавляем их как группу
      if (currentImages.length > 0) {
        currentSection.push({ type: 'imageGroup', images: [...currentImages] })
        currentImages = []
      }
      // Остальные элементы добавляем в текущую секцию
      currentSection.push(block)
    }
  })
  
  // Добавляем последние накопленные изображения
  if (currentImages.length > 0) {
    currentSection.push({ type: 'imageGroup', images: [...currentImages] })
  }
  
  // Добавляем последнюю секцию
  if (currentSection.length > 0) {
    sections.push(currentSection)
  }
  
  // Рендерим секции
  return sections.map((sectionBlocks, sectionIndex) => {
    // Обычная секция
    return (
      <section key={sectionIndex} className={styles.article__section}>
        {sectionBlocks.map((block: any, blockIndex: number) => {
        switch (block.type) {
          case 'cta':
            return (
              <div key={blockIndex} className={styles.article__cta}>
                <Typography variant="p1">
                  {block.text}
                </Typography>
              </div>
            )
          case 'quote':
            // Функция для рендеринга блока внутри quote
            const renderQuoteBlock = (quoteBlock: any, quoteBlockIndex: number) => {
              if (!quoteBlock) return null
              
              switch (quoteBlock.type) {
                case 'heading':
                  const level = quoteBlock.level || 2
                  const variant = level === 2 ? 'h2' : level === 3 ? 'h3' : level === 4 ? 'h4' : level === 5 ? 'h5' : level === 6 ? 'h6' : 'h2'
                  return (
                    <Typography key={quoteBlockIndex} variant={variant as any} className={styles.article__headline}>
                      {quoteBlock.children?.map((child: any) => renderFormattedText(child)) || ''}
                    </Typography>
                  )
                case 'paragraph':
                  return (
                    <Typography key={quoteBlockIndex} variant="p1">
                      {quoteBlock.children?.map((child: any) => renderFormattedText(child)) || ''}
                    </Typography>
                  )
                default:
                  // Если это не известный тип, пробуем рендерить как есть
                  if (quoteBlock.children && Array.isArray(quoteBlock.children)) {
                    return (
                      <Typography key={quoteBlockIndex} variant="p1">
                        {quoteBlock.children.map((child: any) => renderFormattedText(child)) || ''}
                      </Typography>
                    )
                  }
                  // Если это просто текст
                  if (quoteBlock.text) {
                    return (
                      <Typography key={quoteBlockIndex} variant="p1">
                        {quoteBlock.text}
                      </Typography>
                    )
                  }
                  return null
              }
            }
            
            // Определяем содержимое quote блока
            let quoteContent: React.ReactNode = null
            
            if (block.blocks && Array.isArray(block.blocks)) {
              // Кастомный [QUOTE] - массив блоков
              quoteContent = block.blocks.map((quoteBlock: any, quoteBlockIndex: number) => renderQuoteBlock(quoteBlock, quoteBlockIndex))
            } else if (block.children && Array.isArray(block.children)) {
              // Встроенный Quote из Strapi - проверяем, это массив блоков или массив текстовых элементов
              const firstChild = block.children[0]
              if (firstChild && firstChild.type) {
                // Это массив блоков (paragraph, heading и т.д.)
                quoteContent = block.children.map((quoteBlock: any, quoteBlockIndex: number) => renderQuoteBlock(quoteBlock, quoteBlockIndex))
              } else {
                // Это массив текстовых элементов
                quoteContent = (
                  <Typography variant="p1">
                    {block.children.map((child: any) => renderFormattedText(child)) || ''}
                  </Typography>
                )
              }
            } else if (block.content && Array.isArray(block.content)) {
              // Альтернативное поле content
              quoteContent = renderRichTextContent(block.content)
            } else if (block.body && Array.isArray(block.body)) {
              // Альтернативное поле body
              quoteContent = renderRichTextContent(block.body)
            } else if (block.text) {
              // Простой текст
              quoteContent = (
                <Typography variant="p1">
                  {block.text}
                </Typography>
              )
            } else {
              // Последняя попытка - рендерим children как есть
              if (block.children) {
                quoteContent = (
                  <Typography variant="p1">
                    {Array.isArray(block.children) 
                      ? block.children.map((child: any) => renderFormattedText(child)) 
                      : renderFormattedText(block.children)}
                  </Typography>
                )
              }
            }
            
            return (
              <div key={blockIndex} className={styles.article__section}>
                <blockquote className={styles.article__blockquote}>
                  {quoteContent || <Typography variant="p1">Цитата</Typography>}
                </blockquote>
              </div>
            )
          case 'listNumbered':
            return (
              <ol key={blockIndex} className={styles.article__ol}>
                {block.items.map((item: any, itemIndex: number) => (
                  <li key={itemIndex} className={styles.article__ol__item}>
                    <Typography
                      variant="h3"
                      className={styles.article__ol__label}
                      color="accent"
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="p1" className={styles.article__ol__text}>
                      {item.text}
                    </Typography>
                  </li>
                ))}
              </ol>
            )
          case 'paragraph':
            const paragraphChildren = block.children?.map((child: any, childIndex: number) => {
              if (child.type === 'text') {
                return renderFormattedText(child)
              }
              if (child.type === 'link') {
                return (
                  <a key={childIndex} href={child.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'inherit', lineHeight: 'inherit' }}>
                    {child.children?.map((linkChild: any) => renderFormattedText(linkChild))}
                  </a>
                )
              }
              return renderFormattedText(child)
            }) || []
            
            // Проверяем, есть ли ссылки или форматирование в параграфе
            const hasLinks = paragraphChildren.some((child: any) => typeof child !== 'string' && child?.type === 'a')
            const hasFormatting = block.children?.some((child: any) => child.type === 'text' && (child.bold || child.italic || child.underline))
            
            if (hasLinks || hasFormatting) {
              // Если есть ссылки или форматирование, используем Typography с inline-элементами
              return (
                <Typography key={blockIndex} variant="p1">
                  {paragraphChildren}
                </Typography>
              )
            } else {
              // Если нет ни ссылок, ни форматирования, используем Typography с простым текстом
              const paragraphText = paragraphChildren.join('')
              
              return (
                <Typography key={blockIndex} variant="p1">
                  {paragraphText}
                </Typography>
              )
            }
          case 'heading':
            const level = block.level || 2
            const variant = level === 2 ? 'h2' : level === 3 ? 'h3' : level === 4 ? 'h4' : level === 5 ? 'h5' : level === 6 ? 'h6' : 'h2'
            return (
              <Typography key={blockIndex} variant={variant as any} className={styles.article__headline}>
                {block.children?.map((child: any) => renderFormattedText(child)) || ''}
              </Typography>
            )
          case 'list':
            const listTag = block.format === 'ordered' ? 'ol' : 'ul'
            const listClassName = block.format === 'ordered' ? '' : styles.article__ul // Для ul используем кастомные стили, для ol - стандартные
            const itemClassName = block.format === 'ordered' ? '' : styles.article__ul__item
            const items = block.children?.map((item: any, itemIndex: number) => {
              if (block.format === 'ordered') {
                // Для ol используем стандартные стили с Typography
                return (
                  <li key={itemIndex} style={{ marginBottom: '0.5rem' }}>
                      <Typography variant="p1">
                        {item.children?.map((child: any) => renderFormattedText(child)) || ''}
                      </Typography>
                  </li>
                )
              } else {
                // Для ul используем кастомные стили
                return (
                  <li key={itemIndex} className={itemClassName}>
                      <Typography variant="p1">
                        {item.children?.map((child: any) => renderFormattedText(child)) || ''}
                      </Typography>
                  </li>
                )
              }
            }) || []
            return React.createElement(listTag, { 
              key: blockIndex, 
              className: listClassName,
              style: block.format === 'ordered' ? { 
                marginTop: '1rem', 
                marginBottom: '1rem',
                paddingLeft: '1.5rem'
              } : undefined
            }, items)
          case 'imageGroup':
            // Группа изображений - используем ArticleImages
            const images = block.images.map((imgBlock: any) => {
              const imageUrl = imgBlock.image?.url || imgBlock.url || ''
              const altText = imgBlock.image?.alternativeText || imgBlock.alternativeText || ''
              const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : getStrapiImageUrl(imageUrl)
              
              return {
                src: fullImageUrl,
                alt: altText,
                placeholder: 'empty' as const
              }
            })
            
            // Проверяем, есть ли описание в следующем блоке
            let description = ''
            if (blockIndex + 1 < sectionBlocks.length) {
              const nextBlock = sectionBlocks[blockIndex + 1]
              if (nextBlock.type === 'paragraph') {
                const nextText = nextBlock.children?.map((child: any) => child.text || '').join('') || ''
                if (nextText.includes('[DESC]') && nextText.includes('[/DESC]')) {
                  const descMatch = nextText.match(/\[DESC\]([\s\S]*?)\[\/DESC\]/)
                  if (descMatch) {
                    description = descMatch[1].trim()
                    // Удаляем этот блок из рендеринга, так как он уже обработан
                    sectionBlocks.splice(blockIndex + 1, 1)
                  }
                }
              }
            }
            
            return (
              <ArticleImages
                key={blockIndex}
                images={images}
                description={description}
                sizes='100vw'
                row={images.length === 2}
              />
            )
          case 'quote':
            return (
              <div key={blockIndex} className={styles.article__section}>
                <blockquote className={styles.article__blockquote}>
                  {block.children?.map((child: any) => renderFormattedText(child)) || ''}
                </blockquote>
              </div>
            )
          default:
            return null
        }
        })}
      </section>
    )
  })
}

export default function StrapiContentRenderer({ 
  content, 
  faq = [],
  contentImages
}: StrapiContentRendererProps) {
  
  // Если content это Rich Text объект, рендерим его как React компоненты
  if (typeof content === 'object' && content !== null) {
    return (
      <article className={styles.article}>
        {renderRichTextContent(content)}
        
        {/* Добавляем дополнительные изображения из contentImages (начиная со 2-го) */}
        {(() => {
          // Определяем структуру contentImages
          let imagesArray: any[] = [];
          
          if (Array.isArray(contentImages)) {
            // contentImages это массив изображений
            imagesArray = contentImages.slice(1); // Пропускаем первое изображение
          } else if (contentImages?.data && Array.isArray(contentImages.data)) {
            // contentImages имеет структуру { data: [ { attributes: {...} } ] }
            imagesArray = contentImages.data.slice(1); // Пропускаем первое изображение
          }
          
          return imagesArray.length > 0 && (
            <div className={styles.article__section}>
              <ArticleImages
                images={imagesArray.map(img => {
                  const url = img.attributes?.url || img.url;
                  const alt = img.attributes?.alternativeText || img.alternativeText || '';
                  return {
                    src: getStrapiImageUrl(url),
                    alt: alt,
                    placeholder: 'empty' as const
                  };
                })}
                sizes='100vw'
              />
            </div>
          );
        })()}
        
        {/* FAQ секция - только если есть данные */}
        {faq && faq.length > 0 && (
          <section className={styles.article__section}>
            <Accordion type="single" collapsible>
              {faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger>{item.question}</AccordionTrigger>
                  <AccordionContent>{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}
        
        <ShareSection />
      </article>
    )
  }
  
  // Fallback для HTML строки (если контент приходит как HTML)
  const processContent = (html: string) => {
    let processedHTML = html
    
    // Обрабатываем специальные теги [WHITE-TEXT]
    processedHTML = processStrapiTextTags(processedHTML)
    
    // Применяем существующие CSS классы к элементам
    processedHTML = processedHTML.replace(/<h2([^>]*)>/g, '<h2$1 class="article__headline">')
    processedHTML = processedHTML.replace(/<h3([^>]*)>/g, '<h3$1 class="article__headline">')
    processedHTML = processedHTML.replace(/<h4([^>]*)>/g, '<h4$1 class="article__headline">')
    processedHTML = processedHTML.replace(/<h5([^>]*)>/g, '<h5$1 class="article__headline">')
    processedHTML = processedHTML.replace(/<h6([^>]*)>/g, '<h6$1 class="article__headline">')
    processedHTML = processedHTML.replace(/<p([^>]*)>/g, '<p$1 class="article__section">')
    processedHTML = processedHTML.replace(/<ul([^>]*)>/g, '<ul$1 class="article__ul">')
    processedHTML = processedHTML.replace(/<li([^>]*)>/g, '<li$1 class="article__ul__item">')
    processedHTML = processedHTML.replace(/<ol([^>]*)>/g, '<ol$1 class="article__ol">')
    processedHTML = processedHTML.replace(/<li([^>]*)>/g, '<li$1 class="article__ol__item">')
    
    return processedHTML
  }

  const processedContent = processContent(content)

  return (
    <article className={styles.article}>
      <div dangerouslySetInnerHTML={{ __html: processedContent }} />
      
      {/* FAQ секция - только если есть данные */}
      {faq && faq.length > 0 && (
        <section className={styles.article__section}>
          <Accordion type="single" collapsible>
            {faq.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger>{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}
      
      <ShareSection />
    </article>
  )
}
